import { useState } from "react";
import { useConsoleEntryState, useEnterConsoleAfterPayment } from "./useEnterConsoleAfterPayment";
import { useUnpaidInvoicesAccess } from "./useUnpaidInvoicesAccess";
import { useUnpaidPayment } from "./useUnpaidPayment";
import type { ConsoleEntry } from "../../types/unpaidInvoices.types";

const useSupportDialog = () => {
  const [isSupportOpen, setIsSupportOpen] = useState(false);

  return {
    isSupportOpen,
    openSupport: () => setIsSupportOpen(true),
    closeSupport: () => setIsSupportOpen(false),
  };
};

const useTransferReview = (openSupport: () => void) => {
  const [isTransferReviewOpen, setIsTransferReviewOpen] = useState(false);

  return {
    isTransferReviewOpen,
    reviewTransfer: () => setIsTransferReviewOpen(true),
    closeTransferReview: () => setIsTransferReviewOpen(false),
    contactSupportFromTransfer: () => {
      setIsTransferReviewOpen(false);
      openSupport();
    },
  };
};

export type SupportDialog = ReturnType<typeof useSupportDialog>;

export type TransferReview = ReturnType<typeof useTransferReview>;

export const useUnpaidGate = () => {
  const entryState = useConsoleEntryState();
  const access = useUnpaidInvoicesAccess({ refetchInterval: entryState.refetchInterval });
  const isUnpaid = access.accessState === "warning" || access.accessState === "blocked";
  const payment = useUnpaidPayment({
    isEnabled: access.isAdmin && isUnpaid,
    organizationId: access.organizationId,
    context: access.accessState === "blocked" ? "blocked" : "warning",
    noticeAmountCents: access.amountCents,
    paymentMethodRequired: access.paymentMethodRequired,
  });
  const { enterConsole } = useEnterConsoleAfterPayment({
    entry: entryState,
    accessState: access.accessState,
    closePayment: payment.closePayment,
    refreshOrganization: access.refreshOrganization,
  });
  const support = useSupportDialog();
  const transferReview = useTransferReview(support.openSupport);
  const consoleEntry: ConsoleEntry = {
    isEntering: entryState.isEntering,
    hasTimedOut: entryState.hasTimedOut,
    onEnter: enterConsole,
    onContactSupport: support.openSupport,
  };

  return {
    access, payment, consoleEntry, support, transferReview,
  };
};
